import {
  TeammateAvatarEyes,
  TeammateAvatarShape,
} from './teammate-avatar.config';

interface AnimationState {
  x: number;
  y: number;
  rotation: number;
}

export interface ShapeDefinition {
  viewBox: string;
  width: number;
  height: number;
  innerRotation: number;
  regularPath: string;
  hoverState: AnimationState;
  /**
   * Default shape position in the cropped frame, defined for the Small (48 px)
   * baseline. Scale by (frameSize / 48) for x-small and large.
   */
  croppedDefaultState: AnimationState;
  /**
   * Hover shape position in the cropped frame, defined for the Small (48 px)
   * baseline. Scale by (frameSize / 48) for x-small and large.
   */
  croppedHoverState: AnimationState;
  /**
   * Visual size of the shape SVG in the cropped frame at Small (48 px) baseline.
   * Derived from the Figma TeammateShape inset (inset-[11.11%] or inset-[12.5%])
   * scaled from the 72 px base to the 64 px forced container:
   *   pentagon / circle : 56 × (64/72) ≈ 50 px
   *   square / losange  : 54 × (64/72) ≈ 48 px
   *   hexagon           : 60 × (64/72) ≈ 53 px  (image width before 90° rotation)
   */
  croppedContentSize: number;
  /**
   * Eye centre offset from the 64 px wrapper centre in the cropped frame,
   * computed from the per-shape eye positions in the 72 px spec frame:
   *   offsetX = (eyeSpecX + eyeHalfWidth) × (64/72) − 32
   *   offsetY = (eyeSpecY + eyeHalfHeight) × (64/72) − 32
   * where eyeSpec{X,Y} is the top-left of the 28×24 eye container in the spec.
   */
  croppedEyeOffsetX: number;
  croppedEyeOffsetY: number;
  /**
   * Eye-centre offset from the card wrapper's centre, in card units.
   *
   * The card wrapper is 96 px and Figma's `Teammates - with Shadow` (96x128)
   * embeds `Teammate - Base` at 96x96 — i.e. the 72 px spec frame scaled by
   * 4/3, which is the factor this whole coordinate space is built on. So:
   *   offset = (eyeTopLeft + halfEyeBox - 36) * 4/3
   * where the eye box is 28x24 and 36 is the 72 px frame's centre.
   *
   * These are per SHAPE because every shape places its eyes differently
   * (circle's sit 9 px higher than pentagon's in the spec frame).
   */
  cardEyeOffsetX: number;
  cardEyeOffsetY: number;
}

/**
 * Every `Teammate - Eye` variant in Figma is the same 28x24 symbol
 * (`Teammate - Eye`, node 10471:9446) and the artwork sits inside it with
 * per-variant padding — the glyph is never cropped to its own bounding box.
 *
 * So the frame is shared rather than declared per variant, and every eye path
 * below is expressed in these 28x24 units with that padding baked in. Sizing
 * each variant to its own glyph instead is what let eye 3 render 4/3 too large
 * and eye 5's hover dots drift down-left out of the frame's centre.
 *
 * The rendered size is the frame scaled by 4/3 — the same factor that maps
 * Figma's 72px `Teammate - Base` frame onto the 96px card wrapper, so the eye
 * box lands exactly where `cardEyeOffsetX` / `cardEyeOffsetY` place it.
 */
export const EYE_FRAME = {
  viewBox: '0 0 28 24',
  width: (28 * 4) / 3,
  height: (24 * 4) / 3,
} as const;

interface SingleEyeDefinition {
  split?: false;
  regularPath: string;
  hoverPath: string;
}

interface SplitEyeDefinition {
  split: true;
  leftRegularPath: string;
  leftHoverPath: string;
  rightRegularPath: string;
  rightHoverPath: string;
}

export type EyeDefinition = SingleEyeDefinition | SplitEyeDefinition;

const PENTAGON_PATH =
  'M31.286 2.03715C34.892 -0.679052 39.7747 -0.67905 43.3806 2.03715L70.425 22.4087C74.0309 25.1249 75.5398 29.9395 74.1624 34.3344L63.8324 67.2962C62.4551 71.6911 58.5048 74.6667 54.0477 74.6667H20.619C16.1618 74.6667 12.2116 71.6911 10.8343 67.2962L0.504246 34.3344C-0.873091 29.9395 0.635772 25.1249 4.24169 22.4087L31.286 2.03715Z';

const SQUARE_PATH =
  'M0 16C0 7.16344 7.16344 0 16 0H56C64.8366 0 72 7.16344 72 16V56C72 64.8366 64.8366 72 56 72H16C7.16344 72 0 64.8366 0 56V16Z';

const CIRCLE_PATH =
  'M37.3333 0C57.9519 0 74.6667 16.7148 74.6667 37.3333C74.6667 57.9519 57.9519 74.6667 37.3333 74.6667C16.7148 74.6667 0 57.9519 0 37.3333C0 16.7148 16.7148 0 37.3333 0Z';

const HEXAGON_PATH =
  'M28.9482 1.60194C32.4868 -0.533982 36.8466 -0.533981 40.3852 1.60194L63.6148 15.6234C67.1535 17.7593 69.3333 21.7067 69.3333 25.9785V54.0215C69.3333 58.2933 67.1535 62.2407 63.6148 64.3766L40.3852 78.3981C36.8466 80.534 32.4868 80.534 28.9482 78.3981L5.71852 64.3766C2.17989 62.2407 0 58.2933 0 54.0215V25.9785C0 21.7067 2.17989 17.7593 5.71852 15.6234L28.9482 1.60194Z';

// Every path below is placed inside EYE_FRAME's 28x24 box at the coordinates
// Figma gives its artwork node, so the variant's own padding is part of the
// path. The rects are asserted in teammate-avatar.geometry.spec.ts.

// Eye variant '1' — two dots (no hover change). Figma artwork (7, 9) 14x6.
const DOTS_EYES_PATH =
  'M8.99977 9C9.28165 9 9.55184 9.08527 9.79737 9.24316C10.03892 9.39855 10.25755 9.62543 10.43971 9.90821C10.7845 10.44352 11.00001 11.18275 11.00001 12C11.00001 12.81725 10.7845 13.55648 10.43971 14.0918C10.25756 14.37457 10.03892 14.60145 9.79737 14.75683C9.55184 14.91473 9.28165 15 8.99977 15C8.71797 14.99995 8.44835 14.91469 8.20289 14.75683C7.9613 14.60146 7.74273 14.37458 7.56055 14.0918C7.21575 13.55648 7.00025 12.81725 7.00025 12C7.00025 11.18275 7.21575 10.44352 7.56055 9.90821C7.74273 9.62542 7.96129 9.39854 8.20289 9.24316C8.44835 9.08531 8.71797 9.00005 8.99977 9ZM19.00025 9C19.28203 9.00005 19.55165 9.08531 19.79713 9.24316C20.0387 9.39853 20.25725 9.62542 20.43942 9.90821C20.78428 10.44352 20.99975 11.18275 20.99975 12C20.99975 12.81725 20.78428 13.55648 20.43942 14.0918C20.25725 14.37458 20.0387 14.60146 19.79713 14.75683C19.55165 14.91469 19.28203 14.99995 19.00025 15C18.7184 15 18.44817 14.91473 18.20263 14.75683C17.96112 14.60145 17.74243 14.37457 17.56032 14.0918C17.21548 13.55648 17 12.81725 17 12C17 11.18275 17.21548 10.44352 17.56032 9.90821C17.74243 9.62543 17.96112 9.39855 18.20263 9.24316C18.44817 9.08527 18.7184 9 19.00025 9Z';

// Eye variant '2' — wink (split: left=dash, right=wink on hover).
// The left dash is the same 6x3 pill in both Figma states (Variant=2 Hover=No
// 10471:9444 and Hover=Yes 10471:9442 both place it at 4, 10), so it is one
// constant and its morph is a no-op — only the right eye animates.
const WINK_LEFT_PATH =
  'M8.5 10C9.32843 10 10 10.6716 10 11.5C10 12.3284 9.32843 13 8.5 13H5.5C4.67178 12.9998 4 12.3283 4 11.5C4 10.6717 4.67178 10.0002 5.5 10H8.5Z';
const WINK_RIGHT_REGULAR_PATH =
  'M22.50025 10C23.32862 10 24.0001 10.6719 24.00025 11.50049C24.00025 12.32919 23.3287 13.00098 22.50025 13.00098H19.50025C18.67188 13.00089 18.00025 12.32913 18.00025 11.50049C18.0004 10.67196 18.67195 10.00009 19.50025 10H22.50025Z';
const WINK_RIGHT_HOVER_PATH =
  'M21.00025 7C21.82862 7 22.5001 7.67157 22.50025 8.5V11.5C22.50025 12.32843 21.8287 13 21.00025 13C20.17188 13 19.50025 12.32843 19.50025 11.5V8.5C19.5004 7.67157 20.17195 7 21.00025 7ZM20.9995 0C22.0732 0 23.1046 0.41286 23.86623 1.14844C24.43488 1.69776 24.81865 2.39419 24.9766 3.14648C25.13073 3.88112 24.48325 4.3905 23.8699 4.39063C23.19663 4.39049 22.75615 3.86396 22.59032 3.40137C22.5085 3.17319 22.37298 2.96311 22.19193 2.78809C21.877 2.48389 21.44823 2.31055 20.9995 2.31055C20.55092 2.31055 20.12268 2.48389 19.80783 2.78809C19.6267 2.96311 19.49125 3.17319 19.40942 3.40137C19.2436 3.86396 18.80313 4.39049 18.12985 4.39063C17.51643 4.3905 16.86828 3.88112 17.02248 3.14648C17.18042 2.39419 17.56488 1.69776 18.13352 1.14844C18.89507 0.41286 19.92595 0 20.9995 0Z';

// Eye variant '3' — downward curves (regular) → upward curves (hover).
// Figma artwork (5, 10) 18x4 in both states.
const CLOSED_EYES_PATH =
  'M19.35617 13.99988C18.37816 13.99982 17.4388 13.62412 16.74508 12.95398C16.22709 12.45354 15.87744 11.81925 15.73361 11.13391C15.59323 10.46468 16.18335 10.00014 16.74216 10.00012C17.35543 10.00025 17.75636 10.48035 17.90744 10.90173C17.98201 11.10975 18.1056 11.30106 18.27072 11.46057C18.5575 11.73761 18.94755 11.89557 19.35617 11.89563C19.76487 11.89563 20.1555 11.73767 20.44235 11.46057C20.60741 11.30108 20.73109 11.1097 20.80563 10.90173C20.95673 10.48038 21.35767 10.00025 21.97091 10.00012C22.52965 10.00027 23.11912 10.46475 22.97873 11.13391C22.83488 11.81928 22.48529 12.45354 21.96725 12.95398C21.27349 13.62409 20.33419 13.99988 19.35617 13.99988ZM8.64449 13.99988C7.66649 13.99988 6.72719 13.62408 6.03344 12.95398C5.51534 12.45353 5.16502 11.8193 5.02124 11.13391C4.88091 10.4648 5.47102 10.00027 6.02977 10.00012C6.64297 10.00025 7.04392 10.48038 7.19504 10.90173C7.26959 11.1097 7.39326 11.30108 7.55834 11.46057C7.84514 11.73767 8.23582 11.89563 8.64449 11.89563C9.05309 11.89557 9.44317 11.73761 9.72996 11.46057C9.89504 11.30106 10.01864 11.10975 10.09326 10.90173C10.24432 10.48035 10.64527 10.00025 11.25854 10.00012C11.81729 10.00014 12.40747 10.46469 12.26707 11.13391C12.12322 11.81925 11.77356 12.45354 11.25562 12.95398C10.56187 13.62412 9.62249 13.99981 8.64449 13.99988Z';

const CLOSED_EYES_HOVER_PATH =
  'M8.64381 10.00012C9.62184 10.00013 10.56114 10.37591 11.25489 11.04602C11.77288 11.54642 12.1225 12.18079 12.26637 12.86609C12.40679 13.53536 11.81664 13.99988 11.25783 13.99988C10.64466 13.99965 10.2436 13.5196 10.09254 13.09827C10.01798 12.89043 9.89497 12.69885 9.72999 12.53943C9.44316 12.26234 9.05251 12.10511 8.64381 12.1051C8.2351 12.1051 7.84447 12.26233 7.55762 12.53943C7.39264 12.69885 7.26964 12.89042 7.19508 13.09827C7.04402 13.51959 6.64293 13.99962 6.02979 13.99988C5.47097 13.99988 4.88083 13.53536 5.02125 12.86609C5.16512 12.18079 5.51474 11.54641 6.03273 11.04602C6.72648 10.37592 7.66579 10.00012 8.64381 10.00012ZM19.35618 10.00012C20.33418 10.00019 21.27355 10.37589 21.9673 11.04602C22.48533 11.54643 22.8349 12.18078 22.97875 12.86609C23.11915 13.53534 22.52905 13.99986 21.97022 13.99988C21.35695 13.99975 20.956 13.51965 20.80495 13.09827C20.7304 12.89034 20.60672 12.69889 20.44165 12.53943C20.15492 12.26247 19.76478 12.10517 19.35618 12.1051C18.9475 12.1051 18.5569 12.26234 18.27003 12.53943C18.10495 12.69891 17.98135 12.89031 17.90673 13.09827C17.75568 13.51965 17.35473 13.99975 16.74145 13.99988C16.1827 13.99974 15.59253 13.53525 15.73292 12.86609C15.87678 12.18075 16.2271 11.54644 16.74513 11.04602C17.43888 10.37599 18.37825 10.00012 19.35618 10.00012Z';

// Eye variant '4' — vertical lines (no hover change). Figma artwork (8, 8) 12x8.
const LINES_EYES_PATH =
  'M9.5 7.99988C10.32843 7.99988 11 8.67145 11 9.49987V14.50012C10.99986 15.32843 10.32835 16.00013 9.5 16.00013C8.67167 16.00013 8.00013 15.32842 8 14.50012V9.49987C8 8.67146 8.67159 7.99989 9.5 7.99988ZM18.5 7.99988C19.3283 8.00001 20 8.67153 20 9.49987V14.50012C19.99985 15.32835 19.32822 15.99997 18.5 16.00013C17.67162 16.00013 17.00015 15.32843 17 14.50012V9.49987C17 8.67145 17.67155 7.99988 18.5 7.99988Z';

// Eye variant '5' — upward arcs (regular, Figma artwork (5, 10) 18x4) → the
// variant 1 dots on hover (Figma artwork (7, 9) 14x6, the identical asset).
const SMILE_EYES_PATH =
  'M8.64451 10.00012C9.62251 10.00019 10.56188 10.37588 11.25559 11.04602C11.77357 11.54645 12.12325 12.18075 12.26707 12.86609C12.40744 13.5353 11.81731 13.99985 11.25853 13.99988C10.64525 13.99975 10.24432 13.51964 10.09324 13.09826C10.01868 12.89025 9.89508 12.69894 9.72996 12.53942C9.44318 12.26239 9.05312 12.10443 8.64451 12.10436C8.2358 12.10436 7.84517 12.26233 7.55832 12.53942C7.39326 12.69893 7.2696 12.8903 7.19505 13.09826C7.04395 13.51963 6.643 13.99975 6.02977 13.99988C5.47104 13.99973 4.8809 13.53521 5.02122 12.86609C5.16506 12.1807 5.51538 11.54647 6.03343 11.04602C6.72718 10.37592 7.66649 10.00012 8.64451 10.00012ZM19.35617 10.00012C20.33417 10.00012 21.27347 10.37591 21.9673 11.04602C22.48532 11.54646 22.83489 12.18072 22.97874 12.86609C23.11914 13.53524 22.52964 13.99972 21.9709 13.99988C21.35762 13.99975 20.956 13.51964 20.80495 13.09826C20.73032 12.89036 20.6074 12.69888 20.4424 12.53942C20.15552 12.26233 19.76492 12.10436 19.35617 12.10436C18.94757 12.10442 18.55749 12.26239 18.27069 12.53942C18.10562 12.69894 17.98202 12.89025 17.90747 13.09826C17.75634 13.51964 17.35547 13.99975 16.7422 13.99988C16.18337 13.99986 15.59327 13.53532 15.7336 12.86609C15.87745 12.18074 16.22709 11.54645 16.74512 11.04602C17.4388 10.37588 18.37817 10.00018 19.35617 10.00012Z';

export const SHAPE_DEFINITIONS: Record<TeammateAvatarShape, ShapeDefinition> = {
  pentagon: {
    viewBox: '0 0 74.6667 74.6667',
    width: 74.667,
    height: 74.667,
    innerRotation: 0,
    regularPath: PENTAGON_PATH,
    hoverState: {
      x: -3,
      y: -0.77,
      rotation: -4,
    },
    croppedDefaultState: { x: -16, y: 0, rotation: 0 },
    croppedHoverState: { x: -16.15, y: -4.15, rotation: -4 },
    // 56 px shape × (64/72) = 49.78 px — spec §4 eye at (29, 27) in 72 px → centre (43, 39) → ×0.889 → offset (+6.22, +2.67) from 64 px wrapper centre
    croppedContentSize: 50,
    croppedEyeOffsetX: 6.22,
    croppedEyeOffsetY: 2.67,
    // Figma Teammate - Base Shape=Pentagon: eye box at (29, 27) in the 72 px frame
    cardEyeOffsetX: 9.333,
    cardEyeOffsetY: 4.0,
  },
  square: {
    viewBox: '0 0 72 72',
    width: 72,
    height: 72,
    innerRotation: 0,
    regularPath: SQUARE_PATH,
    hoverState: {
      x: 0,
      y: 4,
      rotation: 0,
    },
    croppedDefaultState: { x: -18, y: 2, rotation: 0 },
    croppedHoverState: { x: -16, y: 0, rotation: 0 },
    // 54 px shape × (64/72) = 48 px — spec §4 eye at (29, 26) → centre (43, 38) → ×0.889 → offset (+6.22, +1.78)
    croppedContentSize: 48,
    croppedEyeOffsetX: 6.22,
    croppedEyeOffsetY: 1.78,
    // Figma Teammate - Base Shape=Square: eye box at (29, 26) in the 72 px frame
    cardEyeOffsetX: 9.333,
    cardEyeOffsetY: 2.667,
  },
  circle: {
    viewBox: '0 0 74.6667 74.6667',
    width: 74.667,
    height: 74.667,
    innerRotation: 0,
    regularPath: CIRCLE_PATH,
    hoverState: {
      x: 0,
      y: 4,
      rotation: 0,
    },
    croppedDefaultState: { x: -18, y: 2, rotation: 0 },
    croppedHoverState: { x: -16, y: 0, rotation: 0 },
    // 56 px shape × (64/72) = 49.78 px — spec §4 eye at (29, 18) → centre (43, 30) → ×0.889 → offset (+6.22, -5.33)
    // Circle eyes are positioned significantly higher than other shapes (Y=18 vs Y=26–27)
    croppedContentSize: 50,
    croppedEyeOffsetX: 6.22,
    croppedEyeOffsetY: -5.33,
    // Figma Teammate - Base Shape=Circle: eye box at (29, 18) in the 72 px frame
    cardEyeOffsetX: 9.333,
    cardEyeOffsetY: -8.0,
  },
  hexagon: {
    viewBox: '0 0 69.3333 80',
    width: 69.333,
    height: 80,
    innerRotation: 90,
    regularPath: HEXAGON_PATH,
    hoverState: {
      x: -6,
      y: -2.21,
      rotation: -8,
    },
    croppedDefaultState: { x: -18, y: 2, rotation: 0 },
    croppedHoverState: { x: -20.15, y: -4.15, rotation: -4 },
    // Figma image 60 px wide × (64/72) = 53.33 px — spec §4 eye at (30, 26) → centre (44, 38) → ×0.889 → offset (+7.11, +1.78)
    croppedContentSize: 53,
    croppedEyeOffsetX: 7.11,
    croppedEyeOffsetY: 1.78,
    // Figma Teammate - Base Shape=Hexagon: eye box at (30, 26) in the 72 px frame
    cardEyeOffsetX: 10.667,
    cardEyeOffsetY: 2.667,
  },
  losange: {
    viewBox: '0 0 72 72',
    width: 72,
    height: 72,
    innerRotation: 45,
    regularPath: SQUARE_PATH,
    hoverState: {
      x: -6.21,
      y: -2.21,
      rotation: -8,
    },
    croppedDefaultState: { x: -22, y: 4, rotation: 0 },
    croppedHoverState: { x: -24.14, y: -0.14, rotation: -8 },
    // 54 px square (before 45° rotation) × (64/72) = 48 px — spec §4 eye at (29, 25) → centre (43, 37) → ×0.889 → offset (+6.22, +0.89)
    croppedContentSize: 48,
    croppedEyeOffsetX: 6.22,
    croppedEyeOffsetY: 0.89,
    // Figma Teammate - Base Shape=Losange: eye box at (29, 25) in the 72 px frame
    cardEyeOffsetX: 9.333,
    cardEyeOffsetY: 1.333,
  },
};

export const EYE_DEFINITIONS: Record<TeammateAvatarEyes, EyeDefinition> = {
  '1': {
    regularPath: DOTS_EYES_PATH,
    hoverPath: DOTS_EYES_PATH,
  },
  '2': {
    split: true,
    leftRegularPath: WINK_LEFT_PATH,
    leftHoverPath: WINK_LEFT_PATH,
    rightRegularPath: WINK_RIGHT_REGULAR_PATH,
    rightHoverPath: WINK_RIGHT_HOVER_PATH,
  },
  '3': {
    regularPath: CLOSED_EYES_PATH,
    hoverPath: CLOSED_EYES_HOVER_PATH,
  },
  '4': {
    regularPath: LINES_EYES_PATH,
    hoverPath: LINES_EYES_PATH,
  },
  '5': {
    regularPath: SMILE_EYES_PATH,
    hoverPath: DOTS_EYES_PATH,
  },
};
